<script setup lang="ts">
import { computed } from 'vue';
import { useLang } from '@/composables/useLang';
import { useSiteConfig } from '@/composables/useSiteConfig';

const { isZh } = useLang();
const { config } = useSiteConfig();

const year = new Date().getFullYear();
const author = computed(() => config.value?.authorName ?? config.value?.siteName ?? 'me');
const customFooter = computed(() => config.value?.footerText ?? null);
const icp = computed(() => config.value?.icpBeian ?? null);
const police = computed(() => config.value?.policeBeian ?? null);

// 公安备案号（如"苏公网安备 32011302010001 号"）从字符串里提取 14 位 recordcode
// 拼到 beian.gov.cn 查询链接。提不出就给 # 兜底（保留链接结构防止合规失败）
const policeUrl = computed(() => {
  if (!police.value) return '#';
  const m = police.value.match(/(\d{14})/);
  return m
    ? `http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=${m[1]}`
    : 'http://www.beian.gov.cn/';
});
</script>

<template>
  <footer class="footer">
    <div v-if="customFooter" class="footer__custom">{{ customFooter }}</div>
    <div class="footer__row">
      <span>© {{ year }} {{ author }} · {{ isZh ? '慢慢来' : 'slowly' }}</span>
      <span class="footer__heart">
        <RouterLink to="/settings" class="footer__settings">{{ isZh ? '设置' : 'settings' }}</RouterLink>
        ·
        {{ isZh ? '用爱与咖啡做的' : 'made with coffee' }}
        <span class="footer__icon">♥</span>
      </span>
    </div>
    <div v-if="icp || police" class="footer__beian">
      <a
        v-if="icp"
        href="https://beian.miit.gov.cn"
        target="_blank"
        rel="noopener noreferrer"
        class="footer__beian-link"
      >{{ icp }}</a>
      <a
        v-if="police"
        :href="policeUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="footer__beian-link"
      >{{ police }}</a>
    </div>
  </footer>
</template>

<style scoped>
.footer {
  padding: 48px var(--container-pad-x) 40px;
  border-top: 1px solid rgba(255, 220, 200, 0.08);
  margin: 40px auto 0;
  max-width: var(--container-max);
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(255, 220, 200, 0.5);
}
.footer__row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.footer__custom {
  margin-bottom: 18px;
  font-family: var(--font-display);
  font-style: italic;
  font-size: 13px;
  color: rgba(255, 245, 232, 0.6);
}
.footer__icon {
  color: var(--aurora-accent);
}
.footer__settings {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px dotted rgba(255, 220, 200, 0.3);
  transition: all 0.2s;
}
.footer__settings:hover {
  color: var(--aurora-accent-soft);
  border-bottom-color: rgba(254, 180, 123, 0.4);
}
.footer__beian {
  margin-top: 16px;
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  font-size: 10px;
  color: rgba(255, 220, 200, 0.35);
}
.footer__beian-link {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px dotted transparent;
  transition: all 0.2s;
}
.footer__beian-link:hover {
  color: var(--aurora-accent-soft);
  border-bottom-color: rgba(254, 180, 123, 0.4);
}
</style>
